/**
 * Queue-routing predicate for the native Dexie / iOS write queue.
 *
 * Lives in its own module (not inlined in `api-client.ts`) so it can
 * be unit-tested without pulling in Capacitor, axios, the native
 * queue manager, and the rest of the request pipeline.
 *
 * Decision shape:
 *   - URL matches any QUEUEABLE substring → candidate for queueing
 *   - URL matches any QUEUE_BYPASS regex → forced through to the
 *     server, never queued (bypass wins over queueable)
 *
 * The bypass list exists for endpoints that mutate server-side bytes
 * and have no draft-shape that survives a mock-202 round-trip. The
 * lightbox uploaded-photo rotate is the canonical example: its
 * optimistic state on the client waits for a real Mercure echo from
 * the server (`/users/{uid}/bg_sync/media_objects/rotate/{rotationRequestUUID}`),
 * which only fires after the rotate actually commits. PR #78's build
 * 76 had this endpoint silently queued — the lightbox waited forever.
 */

export const QUEUEABLE_ENDPOINTS: readonly string[] = [
  '/issues',
  '/comments',
  '/media_objects',
  '/locations',
]

export const QUEUE_BYPASS_PATTERNS: readonly RegExp[] = [
  // /media_objects/{id}/rotate — bytes-mutating; requires the real
  // server round-trip for the Mercure echo to land.
  /\/media_objects\/[^/]+\/rotate$/,
]

/**
 * Whether the given URL should route through the native queue. Used
 * by both the request gate and the post-response cache-skip gate in
 * `api-client.ts` so they always agree on which URLs bypassed.
 */
export function isQueueableUrl(url: string): boolean {
  if (QUEUE_BYPASS_PATTERNS.some(re => re.test(url))) return false
  return QUEUEABLE_ENDPOINTS.some(endpoint => url.includes(endpoint))
}
